"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import styles from "./FeaturesSection.module.css";

const features = [
  {
    id: "record",
    title: "Record Screen, Camera & Audio",
    description: "Capture your screen, webcam, and microphone simultaneously in stunning 4K quality at 60fps.",
  },
  {
    id: "edit",
    title: "Edit Videos Fast and Easily",
    description: "Trim, cut, and polish with an intuitive timeline editor. No complexity, just results.",
  },
  {
    id: "embed",
    title: "Embed your videos ",
    description: "",
  },
  {
    id: "analytics",
    title: "Analytics & Data",
    description: "Track views, engagement, and viewer geography in real-time",
  },
  {
    id: "share",
    title: "Share with Instant Links",
    description: "Instantly share recorded or uploaded videos",
  },
  {
    id: "library",
    title: "Cloud Library & Organization",
    description: "All your recordings organized in one place. Search, filter, and access from anywhere.",
  },
];

// Recording Controller Component (based on RecordingController.tsx styling)
function RecordingControllerDemo() {
  const [isPaused, setIsPaused] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);

  return (
    <div className={styles.recordingController}>
      {/* Tooltip */}
      {activeTooltip && (
        <div className={styles.controllerTooltip}>
          {activeTooltip}
        </div>
      )}

      {/* Stop button */}
      <button
        className={styles.stopButton}
        onMouseEnter={() => setActiveTooltip("Stop recording")}
        onMouseLeave={() => setActiveTooltip(null)}
        aria-label="Stop recording"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <rect x="5" y="5" width="14" height="14" rx="3" fill="#ffffff" />
        </svg>
      </button>

      {/* Timer */}
      <span className={styles.timer}>01:24</span>

      {/* Divider */}
      <div className={styles.controllerDivider} />

      {/* Pause/Resume */}
      <button
        className={styles.controlButton}
        onMouseEnter={() => setActiveTooltip(isPaused ? "Resume recording" : "Pause recording")}
        onMouseLeave={() => setActiveTooltip(null)}
        onClick={() => setIsPaused(!isPaused)}
        aria-label={isPaused ? "Resume recording" : "Pause recording"}
      >
        {isPaused ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
          </svg>
        )}
      </button>

      {/* Restart */}
      <button
        className={styles.controlButton}
        onMouseEnter={() => setActiveTooltip("Restart recording")}
        onMouseLeave={() => setActiveTooltip(null)}
        aria-label="Restart recording"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
      </button>

      {/* Trash */}
      <button
        className={styles.controlButton}
        onMouseEnter={() => setActiveTooltip("Delete recording")}
        onMouseLeave={() => setActiveTooltip(null)}
        aria-label="Delete recording"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>
  );
}

// Custom content for the Record card
function RecordCardContent() {
  return (
    <div className={styles.recordCardVisual}>
      {/* Recording controller on the left */}
      <div className={styles.controllerWrapper}>
        <RecordingControllerDemo />
      </div>

      {/* Camera image on the right */}
      <div className={styles.cameraImageWrapper}>
        <picture>
          <source srcSet="https://assets.flare.link/recordingCamera.webp" type="image/webp" />
          <img
            src="https://assets.flare.link/recordingCamera.png"
            alt="Camera recording preview"
            className={styles.cameraImage}
          />
        </picture>
      </div>
    </div>
  );
}

// Sample waveform data - based on real audio, normalized to 0-1
const SAMPLE_WAVEFORM = [
  // Quiet start with tiny variations
  0.02, 0.03, 0.02, 0.04, 0.03, 0.02,
  // First speech burst
  0.18, 0.52, 0.48, 0.55, 0.62, 0.58, 0.45, 0.32,
  // Quick drop
  0.12, 0.08, 0.05, 0.03, 0.02, 0.02, 0.02,
  // Second burst with peaks
  0.22, 0.68, 0.58, 0.72, 0.85, 0.78, 0.92, 0.75, 0.82, 0.68,
  // Sustained medium section (the smooth hill)
  0.55, 0.62, 0.70, 0.75, 0.78, 0.80, 0.78, 0.72, 0.65, 0.55, 0.45,
  // Spiky ending
  0.25, 0.58, 0.18, 0.72, 0.35, 0.85, 0.42, 0.28, 0.65, 0.22,
  // Fade out
  0.38, 0.15, 0.25, 0.08, 0.12, 0.05, 0.03,
];

// Edit Track Demo Component with waveform and scissor cursor
export function EditTrackDemo() {
  const [isHovering, setIsHovering] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [tiltDeg, setTiltDeg] = useState(0);
  const [isCutting, setIsCutting] = useState(false);
  const [scissorProgress, setScissorProgress] = useState(0);
  const trackRef = useRef(null);
  const lastMoveRef = useRef({ x: 0, t: 0 });
  const scissorProgressRef = useRef(0);

  const handleMouseMove = (e) => {
    if (trackRef.current) {
      const rect = trackRef.current.getBoundingClientRect();
      const now = performance.now();
      const prev = lastMoveRef.current;
      const dx = e.clientX - (prev.x || e.clientX);
      const dt = Math.max(1, now - (prev.t || now));
      const vx = dx / dt;
      const targetTilt = Math.max(-12, Math.min(12, vx * 100));
      setTiltDeg((p) => p * 0.8 + targetTilt * 0.2);
      lastMoveRef.current = { x: e.clientX, t: now };
      setCursorPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleMouseDown = () => setIsCutting(true);
  const handleMouseUp = () => setIsCutting(false);

  // Animate scissor open/close
  useEffect(() => {
    if (!isHovering) {
      scissorProgressRef.current = 0;
      setScissorProgress(0);
      return;
    }

    const target = isCutting ? 1 : 0;
    const duration = isCutting ? 100 : 140;
    const start = scissorProgressRef.current;
    const startTime = performance.now();

    let frameId;
    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = start + (target - start) * eased;
      scissorProgressRef.current = current;
      setScissorProgress(current);
      if (t < 1) frameId = requestAnimationFrame(animate);
    };
    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [isCutting, isHovering]);

  // Generate smooth waveform path using cubic bezier curves
  const generateWaveformPath = () => {
    const width = 100;
    const baseline = 88;
    const maxHeight = 72;

    const points = SAMPLE_WAVEFORM.map((value, index) => ({
      x: (index / (SAMPLE_WAVEFORM.length - 1)) * width,
      y: baseline - (value * maxHeight),
    }));

    if (points.length < 2) return "";

    // Start path
    let path = `M 0 ${baseline} L ${points[0].x} ${points[0].y}`;

    // Use smooth cubic bezier curves through points
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1] || curr;
      const prevPrev = points[i - 2] || prev;

      // Calculate control points for smooth curve
      const tension = 0.3;
      const cp1x = prev.x + (curr.x - prevPrev.x) * tension;
      const cp1y = prev.y + (curr.y - prevPrev.y) * tension;
      const cp2x = curr.x - (next.x - prev.x) * tension;
      const cp2y = curr.y - (next.y - prev.y) * tension;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
    }

    // Close the path back to baseline
    path += ` L ${width} ${baseline} L 0 ${baseline} Z`;

    return path;
  };

  return (
    <div className={styles.editTrackWrapper}>
      {/* Time markers above */}
      <div className={styles.timeMarkers}>
        <span>0:00</span>
        <span>0:15</span>
        <span>0:30</span>
        <span>0:45</span>
      </div>

      {/* Track segment */}
      <div
        ref={trackRef}
        className={styles.trackSegment}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => { setIsHovering(false); setIsCutting(false); setTiltDeg(0); }}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        {/* Waveform SVG */}
        <svg
          className={styles.waveformSvg}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveformGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgb(255, 255, 255)" />
              <stop offset="15%" stopColor="rgb(253, 204, 149)" />
              <stop offset="40%" stopColor="rgba(248, 183, 118, 0.9)" />
              <stop offset="60%" stopColor="rgba(255, 150, 80, 0.8)" />
              <stop offset="100%" stopColor="rgba(218, 130, 71, 0.5)" />
            </linearGradient>
          </defs>
          <path
            d={generateWaveformPath()}
            fill="url(#waveformGradient)"
          />
        </svg>

        {/* Scissor cursor when hovering */}
        {isHovering && (
          <div
            className={styles.scissorCursor}
            style={{
              left: cursorPos.x,
              top: cursorPos.y,
              transform: `translate(-16px, -16px) rotate(-90deg) rotate(${tiltDeg.toFixed(1)}deg)`,
            }}
          >
            <svg width="32" height="32" viewBox="-4 -4 32 32" fill="none" style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))' }}>
              <circle cx="12" cy="12" r="1.8" fill="#ffffff" />
              <g transform={`translate(12,12) rotate(${(22 + (4 - 22) * scissorProgress).toFixed(1)})`}>
                <rect x="0" y="-1.4" width="11.5" height="2.8" rx="1.2" fill="#ffffff" />
                <path d="M11.5 -1.4 L14 0 L11.5 1.4 Z" fill="#ffffff" />
                <line x1="-1" y1="0" x2="-7" y2="-3" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" />
                <circle cx="-9.5" cy="-3.6" r="3" fill="none" stroke="#ffffff" strokeWidth="2.2" />
              </g>
              <g transform={`translate(12,12) rotate(${(-1 * (22 + (4 - 22) * scissorProgress)).toFixed(1)})`}>
                <rect x="0" y="-1.4" width="11.5" height="2.8" rx="1.2" fill="#ffffff" />
                <path d="M11.5 -1.4 L14 0 L11.5 1.4 Z" fill="#ffffff" />
                <line x1="-1" y1="0" x2="-7" y2="3" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" />
                <circle cx="-9.5" cy="3.6" r="3" fill="none" stroke="#ffffff" strokeWidth="2.2" />
              </g>
            </svg>
          </div>
        )}

        {/* Playhead line */}
        <div className={styles.playhead} />
      </div>
    </div>
  );
}

// Share Link Demo Component
function ShareLinkDemo() {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.shareLinkWrapper}>
      <button
        className={styles.copyLinkButton}
        onClick={handleClick}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        {copied ? "Copied!" : "Copy link"}
      </button>
    </div>
  );
}

const LIBRARY_DEMO_THUMBNAIL_BASE = "https://videos.flare.link";

function libraryDemoThumbnailUrl(cdnVideoId) {
  return `${LIBRARY_DEMO_THUMBNAIL_BASE}/${encodeURIComponent(cdnVideoId)}/thumbnail-hq.jpg`;
}

// Library Demo Component
export function LibraryDemo() {
  const [activeProject, setActiveProject] = useState("my-flares");
  const [selectedFolder, setSelectedFolder] = useState(null);

  const projects = [
    { id: "my-flares", name: "My flares" },
    { id: "tutorials", name: "Tutorials" },
  ];

  const foldersByProject = {
    "my-flares": [
      { id: "f1", name: "Product demos", color: "#3B82F6", count: 4 },
      { id: "f2", name: "Marketing", color: "#10B981", count: 2 },
    ],
    "tutorials": [
      { id: "f3", name: "Getting started", color: "#F59E0B", count: 3 },
      { id: "f4", name: "Advanced", color: "#8B5CF6", count: 5 },
    ],
  };

  const videosByProject = {
    "my-flares": [
      { id: "v1", cdnVideoId: "1768821637459-etnulyo", title: "Product walkthrough", duration: "2:34", date: "2 days ago", views: 142, comments: 3, folderId: "f1" },
      { id: "v2", cdnVideoId: "1767969166795-iwf7xmv", title: "Feature overview", duration: "1:48", date: "5 days ago", views: 89, comments: 1, folderId: "f1" },
      { id: "v3", cdnVideoId: "1767879487220-7ijolue", title: "Q4 presentation", duration: "5:12", date: "1 week ago", views: 256, comments: 8, folderId: "f2" },
      { id: "v4", cdnVideoId: "1768821637392-7iosr0k", title: "Team update", duration: "3:21", date: "2 weeks ago", views: 67, comments: 2, folderId: "f2" },
    ],
    "tutorials": [
      { id: "v5", cdnVideoId: "1768556353735-lnuaoa6", title: "Quick start guide", duration: "4:15", date: "1 day ago", views: 312, comments: 12, folderId: "f3" },
      { id: "v6", cdnVideoId: "1767692904958-6ifceon", title: "Recording tips", duration: "2:52", date: "3 days ago", views: 198, comments: 5, folderId: "f3" },
      { id: "v7", cdnVideoId: "1767622453332-gz93xyz", title: "Editing basics", duration: "6:30", date: "1 week ago", views: 445, comments: 15, folderId: "f4" },
      { id: "v8", cdnVideoId: "1766262403678-4c6hxxe", title: "Sharing options", duration: "1:45", date: "2 weeks ago", views: 123, comments: 4, folderId: "f4" },
    ],
  };

  const folders = foldersByProject[activeProject] || [];
  const allVideos = videosByProject[activeProject] || [];
  const videos = selectedFolder
    ? allVideos.filter(v => v.folderId === selectedFolder)
    : allVideos;

  // Get folder color for a video
  const getFolderColor = (folderId) => {
    const folder = folders.find(f => f.id === folderId);
    return folder?.color;
  };

  return (
    <div className={styles.libraryDemoWrapper}>
      {/* Project tabs */}
      <div className={styles.libraryProjectTabs}>
        {projects.map((project) => (
          <button
            key={project.id}
            className={`${styles.libraryProjectTab} ${activeProject === project.id ? styles.libraryProjectTabActive : ""}`}
            onClick={() => {
              setActiveProject(project.id);
              setSelectedFolder(null);
            }}
          >
            {project.name}
          </button>
        ))}
      </div>

      {/* Folders */}
      <div className={styles.libraryFolders}>
        {folders.map((folder) => (
          <div
            key={folder.id}
            className={`${styles.libraryFolder} ${selectedFolder === folder.id ? styles.libraryFolderSelected : ""}`}
            onClick={() => setSelectedFolder(selectedFolder === folder.id ? null : folder.id)}
          >
            <span className={styles.libraryFolderColor} style={{ backgroundColor: folder.color }} />
            <span className={styles.libraryFolderName}>{folder.name}</span>
            <span className={styles.libraryFolderCount}>{folder.count}</span>
          </div>
        ))}
      </div>

      {/* Video grid - 2x2 */}
      <div className={styles.libraryVideosGrid}>
        {videos.map((video) => (
          <div key={video.id} className={styles.libraryVideoCard}>
            <div className={styles.libraryVideoThumbnail}>
              {video.cdnVideoId ? (
                <img
                  src={libraryDemoThumbnailUrl(video.cdnVideoId)}
                  alt={video.title}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className={styles.libraryVideoPlaceholder}>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              <span className={styles.libraryVideoDuration}>{video.duration}</span>
            </div>
            <div className={styles.libraryVideoInfo}>
              {getFolderColor(video.folderId) && (
                <span
                  className={styles.libraryVideoFolderBar}
                  style={{ backgroundColor: getFolderColor(video.folderId) }}
                />
              )}
              <div className={styles.libraryVideoTitle}>{video.title}</div>
              <div className={styles.libraryVideoMeta}>
                <span>{video.date}</span>
                <div className={styles.libraryVideoStats}>
                  <span className={styles.libraryVideoStat}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    {video.views}
                  </span>
                  <span className={styles.libraryVideoStat}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {video.comments}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sample retention data for analytics graph demo
const SAMPLE_RETENTION = [
  100, 98, 95, 92, 88, 85, 82, 78, 75, 72,
  70, 68, 65, 62, 60, 58, 55, 52, 50, 48,
  46, 44, 42, 40, 38, 36, 35, 34, 33, 32,
];

// Embed Code Demo Component with IDE-style syntax highlighting
export function EmbedCodeDemo() {
  // Render embed code with VS Code-style syntax highlighting
  return (
    <div className={styles.embedCodeWrapper}>
      <code className={styles.embedCode}>
        <span className={styles.tag}>&lt;div</span>{" "}
        <span className={styles.attr}>id</span>
        <span className={styles.equals}>=</span>
        <span className={styles.string}>"flare-abc123"</span>{" "}
        <span className={styles.attr}>data-flare-video-id</span>
        <span className={styles.equals}>=</span>
        <span className={styles.string}>"abc123"</span>
        <span className={styles.tag}>&gt;</span>
        {"\n"}{"  "}
        <span className={styles.tag}>&lt;iframe</span>{" "}
        <span className={styles.attr}>src</span>
        <span className={styles.equals}>=</span>
        <span className={styles.string}>"https://flarefasthost.com/embed/abc123"</span>
        {"\n"}{"    "}
        <span className={styles.attr}>allow</span>
        <span className={styles.equals}>=</span>
        <span className={styles.string}>"autoplay; fullscreen"</span>
        {"\n"}{"    "}
        <span className={styles.attr}>allowfullscreen</span>{" "}
        <span className={styles.attr}>loading</span>
        <span className={styles.equals}>=</span>
        <span className={styles.string}>"lazy"</span>
        <span className={styles.tag}>&gt;</span>
        {"\n"}{"  "}
        <span className={styles.tag}>&lt;/iframe&gt;</span>
        {"\n"}
        <span className={styles.tag}>&lt;/div&gt;</span>
        {"\n"}
        <span className={styles.tag}>&lt;script</span>{" "}
        <span className={styles.attr}>src</span>
        <span className={styles.equals}>=</span>
        <span className={styles.string}>"https://flarefasthost.com/embed.js"</span>
        <span className={styles.tag}>&gt;&lt;/script&gt;</span>
      </code>
    </div>
  );
}

// Analytics Graph Demo Component
export function AnalyticsGraphDemo() {
  const [hover, setHover] = useState(null);
  const graphRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!graphRef.current) return;
    const rect = graphRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = x / rect.width;
    const idx = Math.max(0, Math.min(SAMPLE_RETENTION.length - 1, Math.floor(ratio * SAMPLE_RETENTION.length)));
    const percent = SAMPLE_RETENTION[idx];
    const timeLabel = `${Math.round((idx / SAMPLE_RETENTION.length) * 60)}s`;
    setHover({ x, percent, timeLabel, side: x > rect.width - 100 ? "left" : "right" });
  };

  const handleMouseLeave = () => setHover(null);

  // Generate smooth path using cubic bezier for one continuous vector line
  const generatePath = () => {
    const width = 100;
    const height = 100;
    const paddingTop = 5;
    const paddingRight = 5;

    const points = SAMPLE_RETENTION.map((val, idx) => ({
      x: (idx / (SAMPLE_RETENTION.length - 1)) * (width - paddingRight),
      y: paddingTop + ((100 - val) / 100) * (height - paddingTop * 2),
    }));

    if (points.length < 2) return "";

    let path = `M ${points[0].x} ${points[0].y}`;

    // Use Catmull-Rom to Bezier conversion for smooth curves
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];

      const tension = 0.35;
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }

    return path;
  };

  const linePath = generatePath();
  const areaPath = `${linePath} L 95 95 L 0 95 Z`;

  return (
    <div className={styles.analyticsGraphWrapper}>
      <div
        ref={graphRef}
        className={styles.analyticsGraph}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className={styles.analyticsGraphSvg}
        >
          <defs>
            <linearGradient id="analyticsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.25" />
              <stop offset="50%" stopColor="#f97316" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          <line x1="0" y1="30" x2="95" y2="30" stroke="#3a3a3c" strokeWidth="0.5" strokeDasharray="2 2" />
          <line x1="0" y1="55" x2="95" y2="55" stroke="#3a3a3c" strokeWidth="0.5" strokeDasharray="2 2" />
          <line x1="0" y1="80" x2="95" y2="80" stroke="#3a3a3c" strokeWidth="0.5" strokeDasharray="2 2" />

          {/* Area fill */}
          <path d={areaPath} fill="url(#analyticsGradient)" />

          {/* Line */}
          <path
            d={linePath}
            fill="none"
            stroke="#f97316"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />

          {/* Hover line */}
          {hover && (
            <line
              x1={(hover.x / (graphRef.current?.offsetWidth || 1)) * 95}
              y1="5"
              x2={(hover.x / (graphRef.current?.offsetWidth || 1)) * 95}
              y2="95"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/* Hover tooltip */}
        {hover && (
          <div
            className={styles.analyticsTooltip}
            style={{
              left: hover.x,
              transform: hover.side === "left" ? "translateX(calc(-100% - 8px))" : "translateX(8px)",
            }}
          >
            <span className={styles.tooltipDot} />
            <span className={styles.tooltipPercent}>{hover.percent}%</span>
            <span className={styles.tooltipTime}>• {hover.timeLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = useCallback((e, index) => {
    const card = cardsRef.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    card.style.setProperty("--mouse-x", `${x}px`);
    card.style.setProperty("--mouse-y", `${y}px`);
  }, []);

  const handleMouseLeave = useCallback((index) => {
    const card = cardsRef.current[index];
    if (!card) return;
    card.style.setProperty("--mouse-x", `-1000px`);
    card.style.setProperty("--mouse-y", `-1000px`);
  }, []);

  return (
    <section className={styles.features}>
      <div className={styles.container}>
        <div className={styles.header} ref={sectionRef}>
          <h2 className={styles.title}>
            One video platform instead of five tools.
          </h2>
          <p className={styles.subtitle}>
            Flare replaces Loom for recording, Wistia for embedding, and Screen Studio for editing.
          </p>
        </div>

        <div className={styles.bentoGrid}>
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={styles.featureCard}
              ref={(el) => (cardsRef.current[index] = el)}
              style={{ transitionDelay: `${index * 80}ms` }}
              onMouseMove={(e) => handleMouseMove(e, index)}
              onMouseLeave={() => handleMouseLeave(index)}
            >
              <div className={styles.cardContent}>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>{feature.description}</p>
              </div>

              {/* Custom content for specific cards, placeholder for others */}
              {feature.id === "record" ? (
                <RecordCardContent />
              ) : feature.id === "edit" ? (
                <div className={styles.imageContainer}>
                  <EditTrackDemo />
                </div>
              ) : feature.id === "share" ? (
                <div className={styles.imageContainer}>
                  <ShareLinkDemo />
                </div>
              ) : feature.id === "analytics" ? (
                <div className={styles.imageContainer}>
                  <AnalyticsGraphDemo />
                </div>
              ) : feature.id === "embed" ? (
                <div className={styles.imageContainer}>
                  <EmbedCodeDemo />
                </div>
              ) : feature.id === "library" ? (
                <div className={styles.imageContainer}>
                  <LibraryDemo />
                </div>
              ) : (
                <div className={styles.imageContainer}>
                  <div className={styles.imagePlaceholder}>
                    <span className={styles.placeholderIcon}>{getIconForFeature(feature.id)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function getIconForFeature(id) {
  const icons = {
    record: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="4" fill="currentColor" />
      </svg>
    ),
    share: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    embed: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    analytics: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
    edit: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
    library: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
    ),
  };
  return icons[id] || null;
}
